/**
 * Cart discount custom elements.
 * Ported from Impact 7.0.1 (js/common/cart/cart-discount.js), rewritten as a self-contained
 * file (no esbuild private-field helpers) so it can live outside theme.js.
 *
 * Applies/removes discount codes via /cart/update.js and dispatches `cart:refresh`, which the
 * cart drawer already listens for and uses to re-render itself.
 */
(() => {
  class AbstractCartDiscount extends HTMLElement {
    async getDiscountCodes() {
      const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      return cart.discount_codes
        .filter((discount) => discount.applicable)
        .map((discount) => discount.code.toLowerCase());
    }

    async toggleDiscount(event) {
      const target = event.currentTarget;
      target.setAttribute("aria-busy", "true");

      let discountCodes = await this.getDiscountCodes();

      if (target.hasAttribute("discount-code")) {
        const toRemove = target.getAttribute("discount-code").toLowerCase();
        discountCodes = discountCodes.filter((discount) => discount !== toRemove);
      }

      const discount =
        (discountCodes.length > 0 ? discountCodes.join(",") + "," : "") + (event.target.value || "");

      let response;
      try {
        response = await fetch(`${Shopify.routes.root}cart/update.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          keepalive: true,
          body: JSON.stringify({ discount }),
        });
      } catch (e) {
        target.setAttribute("aria-busy", "false");
        this.dispatchEvent(new CustomEvent("cart:discount:error", { bubbles: true }));
        return;
      }

      target.setAttribute("aria-busy", "false");

      if (!response.ok) {
        this.dispatchEvent(new CustomEvent("cart:discount:error", { bubbles: true }));
        return;
      }

      const responseJson = await response.json();

      if (responseJson.discount_codes.some((obj) => obj.applicable === false)) {
        this.dispatchEvent(new CustomEvent("cart:discount:error", { bubbles: true }));
        return;
      }

      this.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));

      if (window.themeVariables?.settings?.pageType === "cart") {
        window.location.reload();
      }
    }
  }

  class CartDiscountField extends AbstractCartDiscount {
    constructor() {
      super();
      this._hiddenOriginalValue = this._hiddenInput()?.value || "";
      this.addEventListener("change", this.toggleDiscount.bind(this));
      this.addEventListener("input", this._updateHiddenInput.bind(this));
    }

    _hiddenInput() {
      return this.querySelector('[name="discount"]');
    }

    _updateHiddenInput(event) {
      const hidden = this._hiddenInput();
      if (!hidden) return;
      hidden.value = [this._hiddenOriginalValue, event.target.value]
        .filter((val) => val && val.trim() !== "")
        .join(",");
    }
  }

  class CartDiscountRemoveButton extends AbstractCartDiscount {
    constructor() {
      super();
      this.addEventListener("click", this.toggleDiscount.bind(this));
    }
  }

  class CartDiscountBanner extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      document.addEventListener(
        "cart:discount:error",
        () => {
          this.hidden = false;
        },
        { signal: this._abortController.signal }
      );
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }
  }

  if (!window.customElements.get("cart-discount-field")) {
    window.customElements.define("cart-discount-field", CartDiscountField);
  }
  if (!window.customElements.get("cart-discount-remove-button")) {
    window.customElements.define("cart-discount-remove-button", CartDiscountRemoveButton);
  }
  if (!window.customElements.get("cart-discount-banner")) {
    window.customElements.define("cart-discount-banner", CartDiscountBanner);
  }
})();
